import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    untracked,
    viewChild
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { SiFormFieldsetComponent, SiFormItemComponent } from '@siemens/element-ng/form';
import { SiPillsInputComponent } from '@siemens/element-ng/pills-input';
import {
    SiTreeViewComponent,
    SiTreeViewItemComponent,
    SiTreeViewItemDirective,
    type CheckboxClickEventArgs,
    type TreeItem
} from '@siemens/element-ng/tree-view';
import { Observable, startWith, switchMap } from 'rxjs';
import { CheckboxTreeItemComponent } from './checkbox-tree-item/checkbox-tree-item.component';
import { CheckboxTreeNode } from './checkbox-tree-node.type';
import { CheckboxTreeViewSelectionCategory } from './checkbox-tree-view-selection-category.interface';
import { CheckboxTreeOperations } from './checkbox-tree.operations';

@Component({
    selector: 'tran-checkbox-tree-view-selection',
    imports: [
        SiFormFieldsetComponent,
        SiFormItemComponent,
        SiPillsInputComponent,
        ReactiveFormsModule,
        SiTreeViewComponent,
        SiTreeViewItemComponent,
        SiTreeViewItemDirective,
        CheckboxTreeItemComponent,
        TranslatePipe
    ],
    templateUrl: './checkbox-tree-view-selection.component.html',
    styleUrl: './checkbox-tree-view-selection.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckboxTreeViewSelectionComponent<T> {
    private readonly treeView = viewChild(SiTreeViewComponent);

    public readonly control = input.required<FormControl<T[]>>();
    public readonly displayControl = input<FormControl<string[]>>();
    public readonly items = input<T[]>([]);
    public readonly itemCategory = input<(item: T) => CheckboxTreeViewSelectionCategory | null>(() => null);
    public readonly label = input.required<string>();
    public readonly readonly = input(false, { transform: booleanAttribute });
    public readonly submitted = input(false);
    public readonly itemId = input.required<(item: T) => string | number>();
    public readonly itemLabel = input.required<(item: T) => string>();

    private readonly selectedItems = toSignal(
        toObservable(this.control).pipe(switchMap(ctrl => this.controlValueChanges(ctrl))),
        { initialValue: [] as T[] }
    );
    protected readonly readonlyDisplayControl = new FormControl<string[]>([], { nonNullable: true });
    protected readonly selectedIds = computed(() => new Set(this.selectedItems().map(item => this.itemId()(item))));
    protected readonly isRequired = computed(() => {
        const validator = this.control().validator;
        if (!validator) return false;
        const result = validator(new FormControl(null));
        return result !== null && 'required' in result;
    });

    protected readonly trackTreeItem = (_index: number, item: TreeItem<CheckboxTreeNode<T>>): string => {
        const node = item.customData;
        if (!node) return `other:${item.label ?? ''}`;
        return node.kind === 'category' ? `category:${node.categoryId}` : `item:${this.itemId()(node.item)}`;
    };

    protected readonly treeItems = computed(() =>
        CheckboxTreeOperations.build(this.items(), this.itemLabel(), this.itemCategory())
    );

    constructor() {
        effect(() => {
            this.readonlyDisplayControl.setValue(
                this.selectedItems().map(item => this.itemLabel()(item)),
                {
                    emitEvent: false
                }
            );
        });

        effect(() => {
            const selectedIds = this.selectedIds();
            const treeItems = this.treeItems();
            const treeView = this.treeView();
            const itemId = this.itemId();

            untracked(() => {
                CheckboxTreeOperations.syncSelection(treeItems, selectedIds, itemId);
                treeView?.refresh();
            });
        });
    }

    private isChecked(item: T): boolean {
        return this.selectedIds().has(this.itemId()(item));
    }

    protected onTreeItemCheckboxClicked(event: CheckboxClickEventArgs): void {
        if (this.readonly()) return;

        const node = event.target.customData as CheckboxTreeNode<T> | undefined;
        if (!node || node.kind !== 'item') return;

        this.setItemSelected(node.item, event.newState === 'checked');
    }

    protected onTreeItemClicked(item: TreeItem): void {
        const node = item.customData as CheckboxTreeNode<T> | undefined;
        if (!node) return;

        if (node.kind === 'item') {
            if (!this.readonly()) {
                this.setItemSelected(node.item, !this.isChecked(node.item));
            }
            return;
        }

        const treeView = this.treeView();
        if (!treeView) return;

        if (item.state === 'expanded') {
            treeView.collapseTreeItem(item);
        } else {
            treeView.expandTreeItem(item);
        }
    }

    private controlValueChanges(ctrl: FormControl<T[]>): Observable<T[]> {
        return ctrl.valueChanges.pipe(startWith(ctrl.value ?? []));
    }

    private setItemSelected(item: T, selected: boolean): void {
        const control = this.control();
        const currentValue = control.value ?? [];
        const itemId = this.itemId()(item);
        const updatedItems = selected
            ? currentValue.some(existing => this.itemId()(existing) === itemId)
                ? currentValue
                : [...currentValue, item]
            : currentValue.filter(existing => this.itemId()(existing) !== itemId);

        control.setValue(updatedItems);
        control.markAsTouched();
    }
}
